import { Fragment, useEffect, useState } from "react";
import {
  adminAddQuestion,
  adminAdjustUser,
  adminBanUser,
  adminDeleteQuestion,
  adminGetCurrentSession,
  adminGetQuestions,
  adminGetUsers,
  adminLogin,
  adminLogout,
  adminResetLeaderboard,
  adminSetLeaderboardVisibility,
  adminUnbanUser,
  adminUploadQuestionImage,
  friendlyApiError,
  setAdminCsrfToken,
  adminLoginErrorMessage,
  adminDataError,
  isAdminSessionExpired,
  isInvalidTotpError,
  isPayloadTooLarge,
} from "@/lib/api";
import { csvRow } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { LogIn, Trash2, Download, Bone, Plus, Upload, Loader2, RotateCcw } from "lucide-react";

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState(null); // "r2" | "inline" | null
  const [adjustId, setAdjustId] = useState(null);
  const [grant, setGrant] = useState({ attempts: "", best_score: "", referral_bonus: "" });
  const [granting, setGranting] = useState(false);
  const [newQ, setNewQ] = useState({
    type: "quiz",
    question: "",
    options: ["", "", "", ""],
    correct_index: 0,
    image_url: "",
    points: 5,
  });

  // Pre-launch moderation powers: the launch reset dialog, the per-row
  // leaderboard visibility toggle, and the ban confirmation form.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTyped, setResetTyped] = useState("");
  const [resetBonus, setResetBonus] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [banId, setBanId] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [banning, setBanning] = useState(false);
  const [actionId, setActionId] = useState(null); // row action in flight

  useEffect(() => {
    let active = true;
    adminGetCurrentSession()
      .then((data) => {
        if (!active) return;
        setAdminCsrfToken(data.csrf_token);
        setAuthenticated(true);
      })
      .catch(() => {
        if (active) setAuthenticated(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (authenticated) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  // S2: admin sign-in requires username + password + authenticator code.
  const [totp, setTotp] = useState("");

  const login = async (e) => {
    e.preventDefault();
    try {
      // Username is trimmed (config side is stripped at load); the password
      // and TOTP code are submitted byte-exact (passlib already tolerates
      // spacing in pasted codes).
      await adminLogin(u.trim(), p, totp);
      setAuthenticated(true);
      setTotp("");
      toast.success("Access granted");
    } catch (err) {
      toast.error(adminLoginErrorMessage(err));
      // A rejected authenticator code only clears that field: the admin
      // keeps their username/password to retype just the 6 digits.
      if (isInvalidTotpError(err)) setTotp("");
    }
  };

  // Local-only teardown. Used after a REAL 401 (the session is already
  // dead server-side) so no logout round trip is attempted against it.
  const clearSession = () => {
    setAdminCsrfToken(null);
    setAuthenticated(false);
    setUsers([]);
    setUsersTotal(0);
    setUsersHasMore(false);
    setQuestions([]);
  };

  // A failed data load is NOT automatically an expired session: only a 401
  // is. Network drops, rate limits and 5xx keep the dashboard open and show
  // the cause instead of a false "Session expired" sign-out.
  const handleDataError = (err) => {
    const failure = adminDataError(err);
    toast.error(failure.message);
    if (failure.sessionExpired) clearSession();
  };

  // H5: the admin user list is paginated (newest first, 100/page).
  // The table accumulates pages; CSV export covers loaded rows only.
  const load = async () => {
    try {
      const [uData, qData] = await Promise.all([adminGetUsers(100, 0), adminGetQuestions()]);
      setUsers(uData.users);
      setUsersTotal(uData.total ?? uData.users.length);
      setUsersHasMore(!!uData.has_more);
      setQuestions(qData.questions);
    } catch (err) {
      handleDataError(err);
    }
  };

  const loadMoreUsers = async () => {
    if (loadingUsers || !usersHasMore) return;
    setLoadingUsers(true);
    try {
      const uData = await adminGetUsers(100, users.length);
      setUsers((prev) => [...prev, ...uData.users]);
      setUsersTotal(uData.total ?? users.length);
      setUsersHasMore(!!uData.has_more);
    } catch (err) {
      handleDataError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const logout = async () => {
    await adminLogout().catch(() => undefined);
    clearSession();
  };

  const exportCsv = () => {
    const header = "wallet,twitter,score,referrals,qualified,attempts_used\n";
    const rows = users
      .map((u) =>
        csvRow([
          u.wallet_address || "",
          u.twitter_username || "",
          u.best_score ?? "",
          u.valid_referrals ?? "",
          u.qualified_wl ? "yes" : "no",
          u.attempts_used ?? "",
        ])
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "osteon_participants.csv";
    a.click();
  };

  const addQuestion = async () => {
    if (!newQ.question.trim() || newQ.options.some((o) => !o.trim())) {
      toast.error("Fill all fields");
      return;
    }
    try {
      await adminAddQuestion({ ...newQ, points: newQ.type === "quiz" ? 5 : 10 });
      toast.success("Question added");
      setNewQ({ type: "quiz", question: "", options: ["", "", "", ""], correct_index: 0, image_url: "", points: 5 });
      setUploadMode(null);
      load();
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error("Failed");
    }
  };

  const removeQ = async (id) => {
    try {
      await adminDeleteQuestion(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error("Failed");
    }
  };

  const uploadImage = async (e) => {    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminUploadQuestionImage(file);
      setNewQ((prev) => ({ ...prev, image_url: data.image_url }));
      setUploadMode(data.storage === "r2" ? "r2" : "inline");
      if (data.warning === "r2_cap_reached") {
        toast.warning("R2 storage cap reached — embedded inline instead (no charge)");
      } else {
        toast.success(
          data.storage === "r2"
            ? "Image stored in R2 — cached at the edge"
            : "R2 not configured — image embedded in question"
        );
      }
    } catch (err) {
      // H5: oversized files get a plain message; never server internals.
      toast.error(
        isPayloadTooLarge(err)
          ? "File is too large."
          : err.response?.data?.detail || "Image upload failed"
      );
    } finally {
      setUploading(false);
    }
  };

  const openAdjust = (id) => {
    setAdjustId(id);
    setGrant({ attempts: "", best_score: "", referral_bonus: "" });
  };

  const applyGrant = async (id) => {
    const payload = {};
    for (const key of ["attempts", "best_score", "referral_bonus"]) {
      const raw = grant[key];
      if (raw === "" || raw === null || raw === undefined) continue;
      const value = Number.parseInt(raw, 10);
      if (!Number.isInteger(value) || value < 0) {
        toast.error("Grants must be whole numbers ≥ 0.");
        return;
      }
      payload[key] = value;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Enter at least one grant.");
      return;
    }
    setGranting(true);
    try {
      await adminAdjustUser(id, payload);
      toast.success("Grant applied");
      setAdjustId(null);
      load();
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error(err.response?.data?.detail || "Grant failed");
    } finally {
      setGranting(false);
    }
  };

  // ---- Moderation: launch reset, hide/restore a row, ban/unban ----

  const patchUser = (id, patch) =>
    setUsers((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const closeReset = () => {
    setResetOpen(false);
    setResetTyped("");
    setResetBonus(false);
  };

  // Launch reset wipes competitive standing for EVERY account. It is gated
  // behind typing RESET so it can never fire from a stray click.
  const confirmReset = async () => {
    if (resetTyped.trim().toUpperCase() !== "RESET") {
      toast.error("Type RESET to confirm the launch reset.");
      return;
    }
    setResetting(true);
    try {
      const result = await adminResetLeaderboard({ reset_referral_bonus: resetBonus });
      toast.success(
        `Leaderboard reset — ${result.matched} matched, ${result.modified} updated`
      );
      closeReset();
      load();
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error(friendlyApiError(err, "Leaderboard reset failed"));
    } finally {
      setResetting(false);
    }
  };

  const toggleLeaderboard = async (user) => {
    const hidden = !user.leaderboard_hidden;
    setActionId(user.id);
    try {
      await adminSetLeaderboardVisibility(user.id, hidden);
      patchUser(user.id, { leaderboard_hidden: hidden });
      toast.success(hidden ? "Hidden from the leaderboard" : "Restored to the leaderboard");
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error(friendlyApiError(err, "Could not update leaderboard visibility"));
    } finally {
      setActionId(null);
    }
  };

  const openBan = (id) => {
    setBanId(id);
    setBanReason("");
  };

  const closeBan = () => {
    setBanId(null);
    setBanReason("");
  };

  const confirmBan = async () => {
    const reason = banReason.trim();
    if (reason.length > 500) {
      toast.error("Reason must be 500 characters or fewer.");
      return;
    }
    setBanning(true);
    try {
      const result = await adminBanUser(banId, reason);
      patchUser(banId, { banned: true, ban_reason: reason || null });
      toast.success(
        result.already_banned
          ? "Account was already banned"
          : `Account banned — ${result.sessions_revoked} session(s) revoked`
      );
      closeBan();
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error(friendlyApiError(err, "Ban failed"));
    } finally {
      setBanning(false);
    }
  };

  const unban = async (user) => {
    setActionId(user.id);
    try {
      const result = await adminUnbanUser(user.id);
      patchUser(user.id, { banned: false, ban_reason: null });
      toast.success(result.was_banned ? "Account unbanned" : "Account was not banned");
    } catch (err) {
      if (isAdminSessionExpired(err)) handleDataError(err);
      else toast.error(friendlyApiError(err, "Unban failed"));
    } finally {
      setActionId(null);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#07080c] grid-bg flex items-center justify-center p-6">
        <Toaster theme="dark" />
        <form
          onSubmit={login}
          className="w-full max-w-md cyber-corners bg-[#0d1017]/80 border border-[#1b2234] p-8 clip-terminal"
        >
          <div className="flex items-center gap-3 mb-6">
            <Bone className="w-6 h-6 text-[#00f0ff]" />
            <div>
              <div className="font-mono-cyber text-[10px] uppercase tracking-[0.35em] text-[#00f0ff]">// Admin Access</div>
              <div className="font-heading text-2xl font-bold uppercase">Osteon Control</div>
            </div>
          </div>
          <Label className="font-mono-cyber text-[10px] uppercase tracking-widest text-[#00f0ff]">Username</Label>
          <Input
            data-testid="admin-username"
            autoComplete="username"
            value={u}
            onChange={(e) => setU(e.target.value)}
            className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12"
          />
          <Label className="mt-4 block font-mono-cyber text-[10px] uppercase tracking-widest text-[#00f0ff]">Password</Label>
          <Input
            data-testid="admin-password"
            type="password"
            autoComplete="current-password"
            value={p}
            onChange={(e) => setP(e.target.value)}
            className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12"
          />
          <Label className="mt-4 block font-mono-cyber text-[10px] uppercase tracking-widest text-[#00f0ff]">Authenticator code</Label>
          <Input
            data-testid="admin-totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12"
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-6 w-full rounded-none clip-notch bg-[#00f0ff] hover:bg-[#00f0ff]/90 text-black h-12 font-mono-cyber uppercase tracking-widest text-xs"
          >
            <LogIn className="w-4 h-4 mr-2" /> Enter
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07080c] grid-bg text-slate-100">
      <Toaster theme="dark" />
      <header className="sticky top-0 backdrop-blur-xl bg-[#07080c]/80 border-b border-[#1b2234] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bone className="w-5 h-5 text-[#00f0ff]" />
          <div className="font-heading text-lg font-bold tracking-widest">OSTEON // ADMIN</div>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setResetOpen(true)}
            data-testid="admin-reset-open"
            className="rounded-none clip-notch bg-[#ff2a5f] hover:bg-[#ff2a5f]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset Leaderboard
          </Button>
          <Button onClick={exportCsv} data-testid="admin-export-btn" className="rounded-none clip-notch bg-[#00ff66] hover:bg-[#00ff66]/90 text-black font-mono-cyber uppercase tracking-widest text-xs">
            <Download className="w-4 h-4 mr-2" /> Export Loaded Users
          </Button>
          <Button onClick={logout} variant="outline" className="rounded-none clip-notch bg-transparent border-[#1b2234] font-mono-cyber uppercase tracking-widest text-xs">
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="users">
          <TabsList className="bg-[#0d1017] border border-[#1b2234] rounded-none">
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-none font-mono-cyber uppercase tracking-widest text-xs data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
              Users ({users.length}{usersTotal > users.length ? `/${usersTotal}` : ""})
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions" className="rounded-none font-mono-cyber uppercase tracking-widest text-xs data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
              Questions ({questions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="overflow-x-auto cyber-corners border border-[#1b2234] bg-[#0d1017]/70 clip-terminal">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff] border-b border-[#1b2234]">
                    <th className="p-4">Wallet</th>
                    <th className="p-4">Twitter</th>
                    <th className="p-4">Score</th>
                    <th className="p-4">Refs</th>
                    <th className="p-4">WL</th>
                    <th className="p-4">Attempts</th>
                    <th className="p-4">Leaderboard</th>
                    <th className="p-4">Account</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <Fragment key={u.id || u.wallet_address || u.x_user_id || i}>
                      <tr className="border-b border-[#1b2234]/60 hover:bg-[#131824]">
                        <td className="p-4 font-mono-cyber text-xs">{u.wallet_address || "—"}</td>
                        <td className="p-4 font-mono-cyber text-xs text-slate-400">{u.twitter_username ? `@${u.twitter_username}` : "—"}</td>
                        <td className="p-4 text-[#00ff66] font-bold">{u.best_score}</td>
                        <td className="p-4 text-[#00f0ff] font-bold">{u.valid_referrals ?? 0}</td>
                        <td className="p-4">{u.qualified_wl ? <span className="text-[#00ff66]">✓</span> : <span className="text-slate-500">—</span>}</td>
                        <td className="p-4 text-slate-300">{u.attempts_used}</td>
                        <td className="p-4">
                          {u.leaderboard_hidden ? (
                            <span
                              data-testid={`leaderboard-status-${u.id}`}
                              className="font-mono-cyber text-[10px] uppercase tracking-widest text-[#ff2a5f]"
                            >
                              Hidden
                            </span>
                          ) : (
                            <span
                              data-testid={`leaderboard-status-${u.id}`}
                              className="font-mono-cyber text-[10px] uppercase tracking-widest text-[#00ff66]"
                            >
                              Visible
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <span
                            data-testid={`account-status-${u.id}`}
                            title={u.ban_reason || undefined}
                            className={`font-mono-cyber text-[10px] uppercase tracking-widest ${
                              u.banned ? "text-[#ff2a5f]" : "text-[#00ff66]"
                            }`}
                          >
                            {u.banned ? "Banned" : "Active"}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => (adjustId === u.id ? setAdjustId(null) : openAdjust(u.id))}
                              data-testid={`adjust-toggle-${u.id}`}
                              className="px-3 py-1.5 border border-[#00f0ff]/40 text-[#00f0ff] font-mono-cyber text-[10px] uppercase tracking-widest hover:bg-[#00f0ff]/10"
                            >
                              Grant
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleLeaderboard(u)}
                              disabled={actionId === u.id}
                              data-testid={`toggle-leaderboard-${u.id}`}
                              className="px-3 py-1.5 border border-[#00ff66]/40 text-[#00ff66] font-mono-cyber text-[10px] uppercase tracking-widest hover:bg-[#00ff66]/10 disabled:opacity-50"
                            >
                              {u.leaderboard_hidden ? "Show row" : "Hide row"}
                            </button>
                            {u.banned ? (
                              <button
                                type="button"
                                onClick={() => unban(u)}
                                disabled={actionId === u.id}
                                data-testid={`unban-${u.id}`}
                                className="px-3 py-1.5 border border-[#00f0ff]/40 text-[#00f0ff] font-mono-cyber text-[10px] uppercase tracking-widest hover:bg-[#00f0ff]/10 disabled:opacity-50"
                              >
                                Unban
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openBan(u.id)}
                                data-testid={`ban-open-${u.id}`}
                                className="px-3 py-1.5 border border-[#ff2a5f]/50 text-[#ff2a5f] font-mono-cyber text-[10px] uppercase tracking-widest hover:bg-[#ff2a5f]/10"
                              >
                                Ban
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {adjustId === u.id && (
                        <tr key={`grant-${u.id}`} className="border-b border-[#00f0ff]/30 bg-[#00f0ff]/5">
                          <td colSpan={9} className="p-4">
                            <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff] mb-3">
                              Grant to {u.twitter_username ? `@${u.twitter_username}` : u.id} · used {u.attempts_used} · best {u.best_score} · bonus refs {u.referral_bonus ?? 0}
                            </div>
                            <div className="font-mono-cyber text-[10px] uppercase tracking-widest text-slate-500 mb-3">
                              {/* Mirrors the backend allowance: 3 initial + valid refs + bonus. */}
                              Allowed {3 + (u.valid_referrals ?? 0) + (u.referral_bonus ?? 0)} · remaining{" "}
                              {Math.max(0, 3 + (u.valid_referrals ?? 0) + (u.referral_bonus ?? 0) - (u.attempts_used ?? 0))}
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                              {[
                                { key: "attempts", label: "Attempts (+)", hint: "Adds quiz attempts" },
                                { key: "best_score", label: "Best score (=)", hint: "Sets best (up only)" },
                                { key: "referral_bonus", label: "Referrals (+)", hint: "+1 attempt each" },
                              ].map((field) => (
                                <div key={field.key}>
                                  <Label className="font-mono-cyber text-[10px] uppercase tracking-widest text-slate-400">
                                    {field.label}
                                  </Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={grant[field.key]}
                                    onChange={(e) => setGrant({ ...grant, [field.key]: e.target.value })}
                                    placeholder="0"
                                    data-testid={`grant-${field.key}-${u.id}`}
                                    className="mt-1 bg-[#07080c] border-[#1b2234] rounded-none h-10"
                                  />
                                  <div className="mt-1 font-mono-cyber text-[9px] uppercase tracking-widest text-slate-500">
                                    {field.hint}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 flex gap-3">
                              <Button
                                onClick={() => applyGrant(u.id)}
                                disabled={granting}
                                data-testid={`grant-apply-${u.id}`}
                                className="rounded-none clip-notch bg-[#00ff66] hover:bg-[#00ff66]/90 text-black font-mono-cyber uppercase tracking-widest text-[11px] h-10 px-6"
                              >
                                {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply grant"}
                              </Button>
                              <Button
                                onClick={() => setAdjustId(null)}
                                variant="outline"
                                className="rounded-none clip-notch bg-transparent border-[#1b2234] font-mono-cyber uppercase tracking-widest text-[11px] h-10 px-6"
                              >
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {usersHasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={loadMoreUsers}
                  disabled={loadingUsers}
                  data-testid="users-load-more"
                  className="rounded-none clip-notch bg-transparent border border-[#00f0ff]/40 hover:bg-[#00f0ff]/10 text-[#00ff66] font-mono-cyber uppercase tracking-widest text-[11px] h-10 px-6"
                >
                  {loadingUsers ? <Loader2 className="w-4 h-4 animate-spin" /> : `Load more (${users.length}/${usersTotal})`}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="questions" className="mt-6 space-y-6">
            <div className="cyber-corners bg-[#0d1017]/70 border border-[#1b2234] p-6 clip-terminal">
              <div className="font-heading text-lg font-bold uppercase mb-1">Add Question</div>
              <p className="font-mono-cyber text-[10px] uppercase tracking-widest text-slate-500 mb-4">
                Live immediately · quiz shuffles every attempt · bank total:{" "}
                {questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0)} pts
              </p>
              <div className="grid grid-cols-2 gap-4">
                <select
                  value={newQ.type}
                  onChange={(e) => setNewQ({ ...newQ, type: e.target.value })}
                  className="bg-[#07080c] border border-[#1b2234] p-3 h-12 rounded-none font-mono-cyber text-sm"
                  data-testid="new-q-type"
                >
                  <option value="quiz">Quiz (5 pts)</option>
                  <option value="bone">Bone Guess (10 pts)</option>
                </select>
                <Input
                  placeholder="Image URL (optional)"
                  value={newQ.image_url?.startsWith("data:") ? "" : newQ.image_url}
                  onChange={(e) => setNewQ({ ...newQ, image_url: e.target.value })}
                  className="bg-[#07080c] border-[#1b2234] rounded-none h-12"
                />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label
                  data-testid="new-q-upload"
                  className={`inline-flex items-center gap-2 px-5 h-11 border border-[#1b2234] font-mono-cyber uppercase tracking-widest text-xs cursor-pointer hover:bg-[#1b2234] text-slate-200 ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={uploadImage} className="hidden" />
                </label>
                <span className="font-mono-cyber text-[9px] uppercase tracking-widest text-slate-500">
                  ≤1.5MB · ships inside every quiz fetch, keep it small
                </span>
                {newQ.image_url?.startsWith("data:") && (
                  <span className="font-mono-cyber text-[10px] uppercase tracking-widest text-[#00ff66]">
                    Image attached ✓
                  </span>
                )}
                {uploadMode === "r2" && newQ.image_url && !newQ.image_url.startsWith("data:") && (
                  <span className="font-mono-cyber text-[10px] uppercase tracking-widest text-[#00f0ff]">
                    Stored in R2 · edge-cached ✓
                  </span>
                )}
              </div>
              {newQ.image_url && (
                <div className="mt-4 max-w-sm border border-[#1b2234] overflow-hidden clip-terminal bg-[#07080c]">
                  <img src={newQ.image_url} alt="question preview" loading="lazy" className="w-full max-h-56 object-contain opacity-90" />
                </div>
              )}
              <Input
                placeholder="Question"
                value={newQ.question}
                onChange={(e) => setNewQ({ ...newQ, question: e.target.value })}
                className="mt-4 bg-[#07080c] border-[#1b2234] rounded-none h-12"
                data-testid="new-q-text"
              />
              <div className="mt-4 grid grid-cols-2 gap-3">
                {newQ.options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="correct"
                      checked={newQ.correct_index === i}
                      onChange={() => setNewQ({ ...newQ, correct_index: i })}
                      className="accent-[#00f0ff]"
                    />
                    <Input
                      placeholder={`Option ${i + 1}`}
                      value={o}
                      onChange={(e) => {
                        const opts = [...newQ.options];
                        opts[i] = e.target.value;
                        setNewQ({ ...newQ, options: opts });
                      }}
                      className="bg-[#07080c] border-[#1b2234] rounded-none h-11"
                    />
                  </div>
                ))}
              </div>
              <Button
                onClick={addQuestion}
                data-testid="new-q-submit"
                className="mt-4 rounded-none clip-notch bg-[#00f0ff] hover:bg-[#00f0ff]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
              >
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>

            <div className="space-y-3">
              {questions.map((q) => (
                <div key={q.id} className="p-5 border border-[#1b2234] bg-[#0d1017]/70 flex items-start justify-between gap-4 clip-notch">
                  <div className="flex-1">
                    <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">
                      {q.type} · {q.points} pts
                    </div>
                    <div className="mt-2 font-heading font-semibold">{q.question}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {q.options.map((o, i) => (
                        <span
                          key={i}
                          className={`px-2 py-1 text-xs font-mono-cyber ${
                            i === q.correct_index ? "bg-[#00ff66]/20 text-[#00ff66]" : "bg-[#1b2234] text-slate-400"
                          }`}
                        >
                          {o}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => removeQ(q.id)}
                    className="p-2 border border-[#ff2a5f]/40 text-[#ff2a5f] hover:bg-[#ff2a5f]/10"
                    data-testid={`delete-q-${q.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {resetOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reset leaderboard"
            data-testid="reset-dialog"
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6"
          >
            <div className="w-full max-w-lg bg-[#0d1017] border border-[#ff2a5f]/60 p-6 clip-terminal">
              <div className="font-heading text-lg font-bold uppercase text-[#ff2a5f]">
                Reset leaderboard
              </div>
              <p className="mt-3 font-mono-cyber text-[11px] leading-relaxed text-slate-300">
                Wipes best score, attempts, last attempt and whitelist qualification for EVERY
                account. Accounts, identities, referrals and attempt history are kept.
              </p>
              <div className="mt-4 flex items-start gap-3">
                <Checkbox
                  id="reset-bonus"
                  checked={resetBonus}
                  onCheckedChange={(value) => setResetBonus(value === true)}
                  data-testid="reset-bonus-checkbox"
                  className="mt-0.5"
                />
                <label
                  htmlFor="reset-bonus"
                  className="font-mono-cyber text-[11px] uppercase tracking-widest text-slate-300 cursor-pointer"
                >
                  Also wipe admin referral bonuses
                </label>
              </div>
              <Label
                htmlFor="reset-confirm"
                className="mt-5 block font-mono-cyber text-[10px] uppercase tracking-widest text-[#ff2a5f]"
              >
                Type RESET to confirm
              </Label>
              <Input
                id="reset-confirm"
                data-testid="reset-confirm-input"
                autoComplete="off"
                value={resetTyped}
                onChange={(e) => setResetTyped(e.target.value)}
                className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12"
              />
              <div className="mt-5 flex gap-3">
                <Button
                  onClick={confirmReset}
                  disabled={resetting}
                  data-testid="reset-confirm-btn"
                  className="rounded-none clip-notch bg-[#ff2a5f] hover:bg-[#ff2a5f]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
                >
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset leaderboard"}
                </Button>
                <Button
                  onClick={closeReset}
                  variant="outline"
                  data-testid="reset-cancel-btn"
                  className="rounded-none clip-notch bg-transparent border-[#1b2234] font-mono-cyber uppercase tracking-widest text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {banId && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Ban account"
            data-testid="ban-dialog"
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6"
          >
            <div className="w-full max-w-lg bg-[#0d1017] border border-[#ff2a5f]/60 p-6 clip-terminal">
              <div className="font-heading text-lg font-bold uppercase text-[#ff2a5f]">
                Ban account
              </div>
              <p className="mt-3 font-mono-cyber text-[11px] leading-relaxed text-slate-300">
                Blocks the account and signs out its user sessions. Scores, referrals and
                identity are kept; unban restores access.
              </p>
              <Label
                htmlFor="ban-reason"
                className="mt-5 block font-mono-cyber text-[10px] uppercase tracking-widest text-slate-400"
              >
                Reason (optional, admin only)
              </Label>
              <Input
                id="ban-reason"
                data-testid="ban-reason-input"
                maxLength={500}
                autoComplete="off"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="e.g. coordinated quiz abuse"
                className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12"
              />
              <div className="mt-2 font-mono-cyber text-[9px] uppercase tracking-widest text-slate-500">
                {banReason.length}/500
              </div>
              <div className="mt-5 flex gap-3">
                <Button
                  onClick={confirmBan}
                  disabled={banning}
                  data-testid="ban-confirm-btn"
                  className="rounded-none clip-notch bg-[#ff2a5f] hover:bg-[#ff2a5f]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
                >
                  {banning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ban account"}
                </Button>
                <Button
                  onClick={closeBan}
                  variant="outline"
                  data-testid="ban-cancel-btn"
                  className="rounded-none clip-notch bg-transparent border-[#1b2234] font-mono-cyber uppercase tracking-widest text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
